import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as MuiIcons from '@mui/icons-material';
import journeyTracker from '../utils/journeyTracker';

/**
 * AboutModal - SEO-friendly about section
 * Content is always in DOM for search engine indexability
 * Uses semantic HTML for better SEO
 */
const AboutModal = ({ isOpen, onClose }) => {
  const openTimeRef = useRef(null);

  // Track dwell time on About modal
  useEffect(() => {
    if (isOpen) {
      openTimeRef.current = Date.now();
    } else if (openTimeRef.current) {
      const durationMs = Date.now() - openTimeRef.current;
      if (durationMs >= 1000) {
        journeyTracker.track('section_view', 'About', null, durationMs);
      }
      openTimeRef.current = null;
    }
  }, [isOpen]);
  const dataCategories = [
    {
      icon: 'AccountBalance',
      title: 'Fundamentals & Earnings',
      description: 'Financial metrics, earnings history, analyst estimates, and valuation ratios all in one place.',
      sources: ['P/E ratios', 'Revenue growth', 'Earnings surprises', 'Analyst ratings']
    },
    {
      icon: 'EventNote',
      title: 'Market Events',
      description: 'Economic calendar, Fed meetings, earnings dates, and IPOs that move markets.',
      sources: ['Economic calendar', 'Earnings dates', 'IPO schedule', 'Fed announcements']
    },
    {
      icon: 'DonutSmall',
      title: 'Sector & Money Flow',
      description: 'Visualize how money rotates between sectors and spot emerging trends.',
      sources: ['Sector performance', 'Rotation patterns', 'Industry comparisons']
    },
    {
      icon: 'ShowChart',
      title: 'Technical Patterns',
      description: 'Automated detection of chart patterns and signals that typically require hours of manual analysis.',
      sources: ['Golden/Death crosses', 'Breakouts', 'Support/Resistance', 'Bollinger signals']
    },
    {
      icon: 'Groups',
      title: 'Internal Company Intelligence',
      description: 'Employee sentiment and ratings from Glassdoor reveal what\'s happening inside companies before it shows up in financials.',
      sources: ['Glassdoor ratings', 'Employee reviews', 'CEO approval', 'Hiring trends']
    },
    {
      icon: 'Language',
      title: 'Web Traffic Analytics',
      description: 'Track visitor trends to company websites - a leading indicator of customer interest and business momentum.',
      sources: ['Monthly visitors', 'Traffic trends', 'Engagement metrics']
    }
  ];

  const capabilities = [
    {
      icon: 'Psychology',
      title: 'AI-Powered Lists',
      description: 'Describe what you\'re looking for in plain English and get a curated stock list instantly.'
    },
    {
      icon: 'FilterAlt',
      title: 'Smart Screeners',
      description: 'Pre-built screens combining multiple data points to surface opportunities.'
    },
    {
      icon: 'Notifications',
      title: 'Alerts & Notifications',
      description: 'Get notified when new stocks match your saved criteria.'
    }
  ];

  return (
    <article
      className={`about-modal-container ${isOpen ? 'about-modal-visible' : 'about-modal-hidden'}`}
      aria-hidden={!isOpen}
      itemScope
      itemType="https://schema.org/WebApplication"
    >
      {/* SEO-friendly content - always in DOM */}
      <div className="about-modal-backdrop" onClick={onClose} />

      <section className="about-modal-content" role="dialog" aria-labelledby="about-title">
        <button
          onClick={onClose}
          className="about-modal-close"
          aria-label="Close about dialog"
        >
          <MuiIcons.Close />
        </button>

        <header className="about-modal-header">
          <h2 id="about-title" itemProp="name" className="about-modal-title">
            QuantApe Smart Screener
          </h2>
          <p className="about-modal-tagline" itemProp="description">
            Connect the dots and spot opportunities before others do
          </p>
        </header>

        <div className="about-modal-body">
          <section className="about-section about-video-section">
            <div className="about-video-container">
              <iframe
                src="https://www.youtube.com/embed/0X4W2Y7AMzk"
                title="QuantApe Smart Screener Introduction"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="about-video-iframe"
              />
            </div>
          </section>

          <section className="about-section">
            <h2 className="about-section-title">
              <MuiIcons.Hub className="about-section-icon" />
              Your Quant Assistant, Always On
            </h2>
            <p className="about-section-description">
              The best investment insights come from connecting dots across multiple data sources.
              But gathering employee sentiment, web traffic trends, technical signals, and fundamentals
              takes hours. We do the heavy lifting — aggregating, analyzing, and surfacing
              relevant information exactly when it matters. Like having a super-smart quant
              assistant delivering timely data so you can focus on decisions, not research.
            </p>
          </section>

          <section className="about-section">
            <h2 className="about-section-title">
              <MuiIcons.Layers className="about-section-icon" />
              Data We Integrate
            </h2>

            <div className="about-features-grid" itemProp="featureList">
              {dataCategories.map((category, index) => {
                const IconComponent = MuiIcons[category.icon] || MuiIcons.Star;
                return (
                  <div key={index} className="about-feature-card about-data-card">
                    <div className="about-feature-icon">
                      <IconComponent />
                    </div>
                    <h3 className="about-feature-title">{category.title}</h3>
                    <p className="about-feature-description">{category.description}</p>
                    <div className="about-source-tags">
                      {category.sources.map((source, i) => (
                        <span key={i} className="about-source-tag">{source}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="about-section">
            <h2 className="about-section-title">
              <MuiIcons.AutoAwesome className="about-section-icon" />
              What You Can Do
            </h2>

            <div className="about-capabilities-row">
              {capabilities.map((capability, index) => {
                const IconComponent = MuiIcons[capability.icon] || MuiIcons.Star;
                return (
                  <div key={index} className="about-capability-card">
                    <div className="about-capability-icon">
                      <IconComponent />
                    </div>
                    <h3 className="about-capability-title">{capability.title}</h3>
                    <p className="about-capability-description">{capability.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="about-section about-cta-section">
            <p className="about-cta-text">
              Because insight isn't optional. It's everything.
            </p>
          </section>
        </div>
      </section>
    </article>
  );
};

AboutModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default AboutModal;
